import { Pipe, PipeTransform, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HashtagRoutingService } from '../services/hashtag-routing.service';

@Pipe({
  name: 'sanitizeHtml'
})
export class SanitizeHtmlPipe implements PipeTransform {

  constructor(
    private _sanitizer: DomSanitizer,
    private readonly hashtagRouting: HashtagRoutingService
  ) {
  }

  transform(v: string): SafeHtml {
    const normalized = this.normalizeHtml(v || '');
    const sanitized = this._sanitizer.sanitize(SecurityContext.HTML, normalized) || '';
    return this._sanitizer.bypassSecurityTrustHtml(sanitized);
  }

  private normalizeHtml(value: string): string {
    let output = value;

    // Some Tiptap payloads arrive escaped (&lt;p&gt;...&lt;/p&gt;), which renders as literal tags.
    if (this.looksLikeEscapedHtml(output)) {
      output = this.decodeEscapedHtml(output);
    }

    // Hide editor-only empty paragraphs.
    output = output.replace(/<p>(?:\s|&nbsp;|&#160;|<br\s*\/?>)*<\/p>/gi, '');
    output = this.linkifyHashtags(output);

    return output;
  }

  private linkifyHashtags(value: string): string {
    if (!value) {
      return '';
    }

    if (typeof document === 'undefined') {
      return value.replace(/(^|[^\p{L}\p{N}_>])#([\p{L}\p{N}_-]{2,64})/gu, (_match, prefix, rawTag) => {
        const slug = this.hashtagRouting.normalizeSlug(rawTag);
        return `${prefix}<a class="hashtag-link" href="${this.hashtagRouting.buildTagPath(slug)}">#${slug}</a>`;
      });
    }

    const container = document.createElement('div');
    container.innerHTML = value;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];

    while (walker.nextNode()) {
      const currentNode = walker.currentNode as Text;
      const parentTag = currentNode.parentElement?.tagName?.toLowerCase();
      if (parentTag === 'a' || parentTag === 'script' || parentTag === 'style') {
        continue;
      }
      textNodes.push(currentNode);
    }

    textNodes.forEach((textNode) => {
      const text = textNode.textContent || '';
      if (!/#/.test(text)) {
        return;
      }

      const replaced = text.replace(/(^|[^\p{L}\p{N}_])#([\p{L}\p{N}_-]{2,64})/gu, (_match, prefix, rawTag) => {
        const slug = this.hashtagRouting.normalizeSlug(rawTag);
        return `${prefix}<a class="hashtag-link" href="${this.hashtagRouting.buildTagPath(slug)}">#${slug}</a>`;
      });

      if (replaced === text) {
        return;
      }

      const fragment = document.createElement('span');
      fragment.innerHTML = replaced;
      textNode.replaceWith(...Array.from(fragment.childNodes));
    });

    return container.innerHTML;
  }

  private looksLikeEscapedHtml(value: string): boolean {
    return /&lt;\/?[a-z][\s\S]*?&gt;/i.test(value);
  }

  private decodeEscapedHtml(value: string): string {
    if (typeof document !== 'undefined') {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = value;
      return textarea.value;
    }

    return value
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&amp;/gi, '&')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'");
  }
}
